import React, { useCallback, useEffect, useMemo, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert/dist/components/SweetAlert";
import {
  IAIApiKeyConfigurationEdit,
  IApiKeyConfigurationIndexData,
} from "../../../../models/ai/IAI";
import FloatingLabelDropDown from "../../../../common-components/floatingdropdown";
import Joi from "joi";
import { cloneDeep } from "../../../../constants/commonConstants";
import { sortAlphabetically } from "../../../../common-functions/commonFunctions";
import { UserTypes } from "../../../../models/dashboard/adminDashboard";
import FloatingLabelTextInput from "../../../../common-components/floatingtextinput";
import LoaderSpinner from "../../../../common-components/loader";
import { validateFields } from "../../../../helper/joi-validation";
import { alertConstants, getAlertData } from "../../../../helper/sweet-alert";
import { connect } from "react-redux";
import { updateApiKeyConfiguration } from "../../../../action/ai/aiConfigurationActionCreator";
import {
  getColleges,
  getUserTypes,
} from "../../../../action/common/commonActionCreator";

const ApiKeyConfigurationEdit = React.memo(
  (props: IAIApiKeyConfigurationEdit) => {
    const {
      updateAIApiKeyConfiguration,
      aiApiKeyConfiguration,
      setShowEdit,
      getColleges,
      colleges,
      getAllUserTypes,
      userTypes,
      getAllConfigurations,
    } = props;
    const [data, setData] = useState<IApiKeyConfigurationIndexData>(
      cloneDeep(aiApiKeyConfiguration || {})
    );
    const [errors, setErrors] = useState<any>(undefined);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [alertType, setAlertType] = useState<any>("success");
    const [alertTitle, setAlertTitle] = useState<string>("Default");
    const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const schema = useMemo(
      () =>
        Joi.object().keys({
          collegeId: Joi.number().greater(0).required().label("Select University Management"),
          userType: Joi.number()
            .greater(0)
            .required()
            .label("Select User Type"),
          name: Joi.string().required().label("Name is required"),
          apiKey: Joi.string().required().label("ApiKey is required"),
        }),
      []
    );

    const onChange = useCallback(
      (
        event:
          | React.ChangeEvent<HTMLInputElement>
          | React.ChangeEvent<HTMLSelectElement>
          | React.ChangeEvent<HTMLTextAreaElement>
      ) => {
        errors && delete errors[event.target.name];
        const _data = { ...data, [event.target.name]: event.target.value };
        setData(_data);
      },
      [data, errors]
    );

    const setAlert = useCallback((type: string, name?: string) => {
      let alertData = getAlertData(type, name);
      if (alertData) {
        setShowAlert(true);
        setAlertType(alertData.type);
        setAlertTitle(alertData.title);
      }
    }, []);

    useEffect(() => {
      getColleges();
      getAllUserTypes();
    }, []);

    const onUpdate = useCallback(async () => {
      const validationData = {
        collegeId: data.collegeId,
        userType: data.userType,
        name: data.name,
        apiKey: data.apiKey,
      };
      const validationErrors = validateFields(validationData, schema);
      if (validationErrors === undefined) {
        setLoading(true);
        setIsDisabled(true);

        try {
          const formData = new FormData();
          formData.append("collegeId", data.collegeId.toString());
          formData.append("userType", data.userType.toString());
          formData.append("name", data.name);
          formData.append("apiKey", data.apiKey);

          await updateAIApiKeyConfiguration(formData, data.id, setAlert);
        } finally {
          setLoading(false);
          setIsDisabled(false);
        }
      } else {
        setErrors(validationErrors);
      }
    }, [data, schema, setAlert]);

    const onConfirm = useCallback(() => {
      if (alertType === alertConstants.error) {
        setShowAlert(false);
      } else {
        getAllConfigurations && getAllConfigurations();
        setShowEdit(false);
      }
    }, [alertType]);

    return (
      <>
        <div className="modal-header">
          <h1 className="modal-title fs-5" id="addnewbuttonsLabel">
            Update AI Configuration
          </h1>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => setShowEdit(false)}
          ></button>
        </div>
        <div className="modal-body">
          <div className="row">
            <div className="col-md-6 form-group">
              <FloatingLabelDropDown
                label={"University Management"}
                onChange={onChange}
                isMandatory={true}
                value={data.collegeId}
                name={"collegeId"}
                optionsList={sortAlphabetically(colleges) || []}
              />
              {errors?.collegeId && (
                <div className="text-danger">{errors.collegeId}</div>
              )}
            </div>

            <div className="col-md-6 form-group">
              <FloatingLabelDropDown
                label={"User Type "}
                onChange={onChange}
                isMandatory={true}
                value={data.userType}
                name={"userType"}
                optionsList={
                  sortAlphabetically(
                    userTypes?.filter((type) => type.id !== UserTypes.Freshman)
                  ) || []
                }
              />
              {errors?.userType && (
                <div className="text-danger">{errors.userType}</div>
              )}
            </div>

            <div className="col-md-6 form-group" title="A unique secret used to authenticate your application with the AI provider. Obtain it from your AI provider and keep it confidential.">
              <FloatingLabelTextInput
                label={"Api Key"}
                onChange={onChange}
                isMandatory={true}
                value={data.apiKey}
                name={"apiKey"}
              />
              {errors?.userType && (
                <div className="text-danger">{errors.apiKey}</div>
              )}
            </div>
            <div className="col-md-6 form-group">
              <FloatingLabelTextInput
                label={"Name"}
                onChange={onChange}
                isMandatory={true}
                value={data.name}
                name={"name"}
              />
              {errors?.userType && (
                <div className="text-danger">{errors.name}</div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onUpdate}
              disabled={isDisabled}
            >
              Update
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              data-bs-dismiss="modal"
              onClick={() => setShowEdit(false)}
              disabled={isDisabled}
            >
              Close
            </button>
          </div>
        </div>
        <SweetAlert
          show={showAlert}
          success={false}
          type={alertType}
          title={alertTitle}
          onConfirm={() => onConfirm()}
          showCancel={showAlertCancel}
          onCancel={() => {
            setShowAlert(false);
            setShowAlertCancel(false);
          }}
          cancelBtnText={"No"}
          confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
        ></SweetAlert>
        {loading && <LoaderSpinner />}
      </>
    );
  }
);

const mapDispatchToProps = {
  updateAIApiKeyConfiguration: (data: any, id: number, callBack: Function) =>
    updateApiKeyConfiguration(data, id, callBack),
  getColleges: () => getColleges(),
  getAllUserTypes: () => getUserTypes(),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    userTypes: state.common.userTypes,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ApiKeyConfigurationEdit);
